import type { CSSProperties, KeyboardEvent, ReactElement, JSX } from 'react';
import {
  Children,
  cloneElement,
  useEffect,
  useRef,
  useState,
  useContext,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { isNonEmptyString, isNotUndefined, isUndefined } from '~/utilities/type-guards';
import { useSnowplow } from '~/onboarding/hooks/useSnowplow/useSnowplow';
import { useTargetObserver } from '~/onboarding';
import { AnimatedSpot } from './AnimatedSpot';
import type { Position, Targetable, Trackable } from '../../types';
import { calculatePosition } from '../../hooks/useObserveElementRect/calculatePosition';
import { useTargetInteraction } from '../../hooks/useTargetInteraction';
import { useLogger } from '../../hooks/useLogger';
import { withErrorBoundary, useErrorBoundary } from '../../hooks/useErrorBoundary';
import { OnboardingContext } from '../../hooks/useOnboardingContext/OnboardingContext';
import type { HotspotColor } from './types';
import { useHotspotThemeColor } from './useHotspotThemeColor';
import type { TooltipProps } from '../Tooltip/Tooltip';

type ContainerProps = {
  top?: number;
  left?: number;
  color: string;
  zIndex?: CSSStyleDeclaration['zIndex'];
};
export const Container = styled.svg.attrs<ContainerProps>(({ top, left }) => ({
  style: { left, top },
}))<ContainerProps>`
  border-radius: 26px;
  outline: none;
  position: absolute;
  transform: translate(-50%, -50%);
  z-index: ${({ zIndex }) => zIndex};

  &:focus-visible {
    outline: thin dotted ${({ color }) => color};
  }
`;

export type HotspotProps = Targetable &
  Trackable & {
    /**
     * Where on the target/child to position the hotspot.
     * We recommend 'top-right' as the standard for consistency.
     */
    position?: Position;
    color?: HotspotColor;
    /**
     * Whether or not the Hotspot's target should be activated when the Hotspot is clicked.
     * If true, a click event will be dispatched for the target.
     */
    activateTargetOnClick?: boolean;
    children?: JSX.Element | JSX.Element[] | React.ReactNode;
    tooltip?: ReactElement<TooltipProps>;
    dismissOnHover?: boolean;
    /**
     * Set the z-index for the Hotspot.
     * **Note:** If rendering a Hotspot within a modal or dropdown, this will need to be set;
     * 1000 should be sufficient.
     */
    zIndex?: CSSStyleDeclaration['zIndex'];
    style?: CSSProperties;
    hideOnHover?: boolean; // calls Context.hide() to hide the whole experience on mouse hover
  };

/**
 * ### Purpose & Usage
 * This visual cue serves as a guiding beacon during feature onboarding, ensuring users
 * easily identify new interactive components, or signal that there is additional info to
 * be learned. These hotspots can be dismissed on hover or click and can also show
 * additional info in the form of a tooltip.
 *
 * ### Default Settings
 * **Color:**
 * * Default: Should use pink color (pink500).
 * * Customization: While the default pink is preferred for consistency, it can be altered
 * to fit the specific design requirements of the component or the overall application theme.
 *
 * **Position:**
 * * Default: Should be positioned at the top right corner of the component.
 * * Customization: The location can be adjusted to better suit the design layout or user
 * experience needs, but any changes should be made thoughtfully to ensure the hotspot
 * remains easily noticeable.
 *
 * Consider the overall design cohesion and user experience when deviating, the props are
 * there to be used when the defaults do not work in specific situations where pink wouldn’t
 * be noticeable on the element or contextually on the page.
 */
export const HotspotComponent = ({
  getTarget,
  position = 'top-right',
  color = 'pink',
  activateTargetOnClick = false,
  children,
  tooltip,
  dismissOnHover = false,
  zIndex,
  style = {},
  hideOnHover = false,
  snowplowParams,
}: HotspotProps): JSX.Element | null => {
  const context = useContext(OnboardingContext);
  const currentFeatureId = context?.getCurrentFeatureId();
  const logger = useLogger(currentFeatureId);
  useErrorBoundary((error: Error) => logger.error(error));

  const [active, setActive] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hotspotRef = useRef<SVGSVGElement | null>(null);
  const hotspotColor = useHotspotThemeColor(color);
  const { rect, target } = useTargetObserver(
    getTarget ?? (() => containerRef.current),
    isNotUndefined(getTarget),
  );
  const { top, left } = calculatePosition(position, rect);
  const { logEvent, logEventOnce } = useSnowplow();
  const interruptTargetInteraction = useRef(false);
  useTargetInteraction(
    (event) => {
      if (event.type === 'click') {
        logEvent('hotspotClick', snowplowParams);

        const activateTarget = activateTargetOnClick && target instanceof HTMLElement;

        // If we're manually activating the target or the target is a child of the
        // Hotspot (e.g., consumer isn't using getTarget to manage interactions), interrupt
        // the interaction so that clicking the Hotspot doesn't auto-dismiss it.
        if (activateTarget || isUndefined(getTarget)) {
          interruptTargetInteraction.current = true;
        }

        if (activateTarget) {
          target.click();
        }
      }
    },
    () => hotspotRef.current,
  );

  useEffect(() => {
    logEventOnce(active ? 'hotspotView' : 'hotspotDismiss', snowplowParams);
  }, [active, logEventOnce, snowplowParams]);

  useEffect(() => {
    const listener = () => {
      if (interruptTargetInteraction.current) {
        interruptTargetInteraction.current = false;
      } else {
        tooltip?.props.onDismiss?.();
        setActive(false);
      }
    };

    target?.addEventListener('click', listener);
    if (dismissOnHover) {
      target?.addEventListener('mouseover', listener);
    }

    return () => {
      target?.removeEventListener('click', listener);
      target?.removeEventListener('mouseover', listener);
    };
  }, [dismissOnHover, target, tooltip?.props]);

  const onKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      hotspotRef.current?.blur();
    }
  }, []);

  const onMouseOver = useCallback(() => {
    if (hideOnHover && isNonEmptyString(currentFeatureId)) {
      context?.hide(currentFeatureId);
    }
    if (dismissOnHover) {
      setActive(false);
    }
  }, [dismissOnHover, hideOnHover, currentFeatureId, context]);

  return (
    <div
      data-testid="onboarding-hotspot"
      ref={containerRef}
      style={Children.count(children) > 0 ? { display: 'inline-block' } : {}}
    >
      {isNotUndefined(rect) && active ? (
        <Container
          ref={hotspotRef}
          top={top}
          left={left}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseOver={onMouseOver}
          color={hotspotColor}
          role="button"
          width="53"
          height="53"
          viewBox="0 0 53 53"
          fill="none"
          zIndex={zIndex}
          style={style}
        >
          <AnimatedSpot
            opacity="0.1"
            color={color}
            radius={26}
          />
          <AnimatedSpot
            opacity="0.2"
            color={color}
            radius={17}
          />
          <circle
            cx="26.8125"
            cy="26.8125"
            r="6"
            fill={hotspotColor}
          />
        </Container>
      ) : null}
      {active && isNotUndefined(tooltip)
        ? cloneElement(tooltip, {
            hotspotTop: top,
            hotspotLeft: left,
            getTarget: () => hotspotRef.current,
            onDismiss: () => {
              tooltip.props.onDismiss?.();
              setActive(false);
            },
          })
        : null}
      {children}
    </div>
  );
};

/**
 * @deprecated Use OnboardingPopover or OnboardingMultiStepPopover instead.
 */
export const Hotspot = withErrorBoundary(HotspotComponent);
